// Tourism category configurations for Anne Flinn Advertising.
// Each slug becomes a route: /tourism/<slug>

export const tourismCategories = {
  dmos: {
    slug: "dmos",
    name: "Destination Marketing Organizations",
    shortName: "DMOs",
    icon: "Globe",
    heroEyebrow: "FOR TOURISM BOARDS, CVBs & DMOs",
    heroHeadline: "Drive the visitors. Prove the impact. Renew the funding.",
    heroSub:
      "Multi-state, omni-channel campaigns for tourism boards, CVBs, and destination marketing organizations. Built on real strategy, and proven results like 45,000+ website clicks for Nelson County Tourism.",
    painPoints: [
      "Grant funding depends on measurable ROI, and your board needs proof, fast",
      "Competing with bigger DMOs in DC, Richmond, Asheville, and beyond",
      "Multi-state campaigns require coordination most agencies can't deliver",
    ],
    seasonalTriggers: [
      {
        season: "Spring + Fall peaks",
        trigger:
          "Wanderlove, Eat. Drink. Love., and seasonal campaigns layer in",
      },
      {
        season: "Off-season",
        trigger: "Build brand awareness when competitors go dark",
      },
      {
        season: "Event windows",
        trigger: "Pulse campaigns around festivals, exhibits, signature events",
      },
    ],
    packageExamples: {
      topOfMind:
        "Build top-of-mind for your destination across regional DMAs. Streaming TV positions you alongside Travel Channel, HGTV, and Food Network, where travelers plan.",
      callNow:
        "Geo-target out-of-state DMAs (DC, Richmond, Charlotte, Raleigh). Run PMAX Ads across all of Google 24/7 with seasonal creative swaps for peak, shoulder, and holiday windows.",
      marketDominator:
        "Streaming + PMAX combo with multiple campaign concepts running in sync, the model we used to drive 45,000+ website visits, +1,700% Facebook reach, and +4,700% Instagram reach for Nelson County.",
    },
    testimonial: {
      quote:
        "Anne Flinn Advertising is particularly effective at working with public-sector organizations, navigating stakeholder considerations, and developing messaging that balances policy objectives with public understanding. On a scale of 1 to 10, I would rate their performance a 10.",
      author: "Maureen Kelley, Director of Economic Development & Tourism, Nelson County, VA",
    },
  },

  "wineries-breweries": {
    slug: "wineries-breweries",
    name: "Wineries, Breweries & Distilleries",
    shortName: "Wineries & Breweries",
    icon: "Wine",
    heroEyebrow: "FOR WINERIES, BREWERIES, CIDERIES & DISTILLERIES",
    heroHeadline: "Be the next stop on every Virginia trail itinerary.",
    heroSub:
      "Built for tasting-room operators. These winery, brewery, cidery, and distillery campaigns have been refined across years of actual Virginia wine-trail work. You'll have confidence in your ad buy and can focus on what you do best: pouring, hosting, and growing your wine club. We make the marketing easy, we won't waste your VTC grant dollars, and we know exactly how to report back when audit time comes around.",
    painPoints: [
      "Tasting-room visits are competitive, Charlottesville alone has 30+ wineries within 30 miles",
      "Wine club retention is the lifeblood, but visitor acquisition is what feeds it",
      "Events and pickup parties are huge revenue drivers, if visitors know about them",
    ],
    seasonalTriggers: [
      {
        season: "Spring",
        trigger: "Mother's Day, spring release, and weekend brunch campaigns",
      },
      {
        season: "Summer + Fall",
        trigger: "Harvest, fall foliage, and weekend driving-tour ads",
      },
      {
        season: "Holiday season",
        trigger: "Wine-club gift, pickup party, and holiday tasting-flight ads",
      },
    ],
    packageExamples: {
      topOfMind:
        "Reach travelers planning Charlottesville and Shenandoah getaways on Travel, HGTV, Food Network, and local news.",
      callNow:
        "Target Northern Virginia, DC, and surrounding metros with hyper-local PMAX campaigns that reach visitors actively planning wine trips.",
      marketDominator:
        "Streaming for brand-build + PMAX for the visit decision. Best for properties with events, weddings, or wine-club growth goals.",
    },
    testimonial: null,
  },

  "hotels-inns": {
    slug: "hotels-inns",
    name: "Hotels, Inns & B&Bs",
    shortName: "Hotels & Inns",
    icon: "BedDouble",
    heroEyebrow: "FOR HOTELS, INNS, B&Bs & RESORTS",
    heroHeadline: "Fill the rooms. Beat the OTAs. Own the booking.",
    heroSub:
      "Streaming + PMAX advertising for boutique hotels, inns, B&Bs, and resorts. Drive direct bookings, build brand recall, and reduce dependence on Expedia and Booking.com commissions.",
    painPoints: [
      "OTAs (Expedia, Booking.com) take 15–25% per booking, every direct booking is pure margin",
      "Brand-name hotels in nearby metros pull marketing dollars that boutique properties can't match",
      "Shoulder season is where the margin lives, and where most marketing dies",
    ],
    seasonalTriggers: [
      {
        season: "Peak season",
        trigger: "Saturday-night premium-rate campaigns with last-room urgency",
      },
      {
        season: "Shoulder season",
        trigger: "Romance, getaway, and 'midweek escape' ads to out-of-state DMAs",
      },
      {
        season: "Wedding + event windows",
        trigger: "Room-block ads tied to local festivals, weddings, and games",
      },
    ],
    packageExamples: {
      topOfMind:
        "Build brand recall as a destination on HGTV, Travel, and Food Network, the channels travelers use to plan their trips.",
      callNow:
        "Geo-target high-income ZIP codes in DC, Richmond, Charlotte. Retarget anyone who visited your site without booking. Drive direct, OTA-free.",
      marketDominator:
        "Full-funnel: streaming for awareness + PMAX for the booking decision. Best for properties with $200+ ADR or strong event/wedding business.",
    },
    testimonial: null,
  },

  attractions: {
    slug: "attractions",
    name: "Attractions & Experiences",
    shortName: "Attractions",
    icon: "Ticket",
    heroEyebrow:
      "FOR MUSEUMS, HISTORIC SITES, OUTDOOR ADVENTURE & EXPERIENCES",
    heroHeadline:
      "Turn 'I should visit there sometime' into ticket sales this weekend.",
    heroSub:
      "Streaming + PMAX advertising for museums, historic sites, outdoor adventure, theaters, and experience-based attractions. Drive ticket sales, memberships, and repeat visits.",
    painPoints: [
      "Tickets are decided in the moment, whoever's most visible wins the day-trip decision",
      "Exhibit openings, seasonal programming, and limited-time runs need fast awareness pushes",
      "Competing with free / low-cost alternatives, your brand has to feel worth the price",
    ],
    seasonalTriggers: [
      {
        season: "School out of session",
        trigger: "Family-day, summer-camp, and weekend-trip campaigns",
      },
      {
        season: "Fall foliage + holidays",
        trigger:
          "Seasonal exhibit, holiday lights, and 'last weekend to see' ads",
      },
      {
        season: "New exhibit launches",
        trigger: "Pulse campaigns 4–6 weeks pre-opening to build buzz",
      },
    ],
    packageExamples: {
      topOfMind:
        "Position your attraction as a must-see on Travel, HGTV, History, and local news.",
      callNow:
        "Geo-target weekend-day-trip DMAs. Live 'distance-to' tech shows visitors how close you are. Swap creative weekly for new exhibits or seasonal events.",
      marketDominator:
        "Brand-build for the long arc + lead-gen for the weekend rush. The same multi-campaign model that drove Nelson County's Smithsonian exhibit to the highest visitation in the Commonwealth.",
    },
    testimonial: {
      quote:
        "The Smithsonian staff noted it was one of the best campaigns they had seen nationwide. Anne's team delivered, full stop.",
      author: "Maureen Kelley, Economic Development Director, Nelson County Tourism",
    },
  },

  restaurants: {
    slug: "restaurants",
    name: "Restaurants & Culinary",
    shortName: "Restaurants",
    icon: "Utensils",
    heroEyebrow: "FOR RESTAURANTS, FOOD HALLS & CULINARY DESTINATIONS",
    heroHeadline: "Be the reservation visitors book before they leave home.",
    heroSub:
      "Streaming + PMAX advertising for restaurants, food halls, and culinary destinations targeting visitors and locals. Fill the dining room every night, not just weekends.",
    painPoints: [
      "Visitors decide where to eat 24–48 hours before arrival, that's the window to win",
      "OpenTable & Resy show you next to 50 competitors, paid air cover changes the math",
      "Weekday covers are where the margin dies; weekday-targeted campaigns fix that",
    ],
    seasonalTriggers: [
      {
        season: "Tourism peaks",
        trigger:
          "Fall foliage, summer, and holiday weeks get full creative pushes",
      },
      {
        season: "Special menus",
        trigger:
          "Restaurant Week, seasonal tasting menus, and chef-collab events",
      },
      {
        season: "Year-round",
        trigger:
          "Weekday cover-boost campaigns and pre-arrival visitor targeting",
      },
    ],
    packageExamples: {
      topOfMind:
        "Build brand desire on Food Network, Travel, and HGTV, visitors and locals hire the restaurant they've been seeing all year.",
      callNow:
        "Geo-target visitors *before* they arrive (DC, Richmond, Charlotte) and *while* they're here (live 'distance-to' tech). Drive the reservation.",
      marketDominator:
        "Awareness + intent campaigns running in tandem. Best for higher-ticket dining and culinary destinations with multiple revenue streams.",
    },
    testimonial: null,
  },

  "events-festivals": {
    slug: "events-festivals",
    name: "Events, Festivals & Venues",
    shortName: "Events & Festivals",
    icon: "CalendarHeart",
    heroEyebrow: "FOR EVENT VENUES, FESTIVALS & TICKETED EXPERIENCES",
    heroHeadline: "Sell out the event. Build the brand for the next one.",
    heroSub:
      "Streaming + PMAX advertising for music festivals, fairs, ticketed events, and event venues. Hit ticket-sales goals, and build the audience for next year.",
    painPoints: [
      "Tickets sell in waves, Early Bird, Pre-Sale, GA, VIP, and each wave needs different creative",
      "Multi-day festivals require multi-state reach to fill camping + hotel-block goals",
      "Wedding venues and event spaces compete with Instagram, your ads need to look that good",
    ],
    seasonalTriggers: [
      {
        season: "6 months out",
        trigger: "Brand-build + 'save the date' campaigns to regional DMAs",
      },
      {
        season: "3 months out",
        trigger: "Early-bird ticket and lineup-reveal creative cycles",
      },
      {
        season: "Final 4 weeks",
        trigger: "Real-time last-call, FOMO, and live-from-the-event ads",
      },
    ],
    packageExamples: {
      topOfMind:
        "Brand-build streaming for ticket waves + brand recall for next year's event.",
      callNow:
        "Geo-target out-of-state DMAs with live 'distance-to' tech. Run different creative for Early Bird, GA, and VIP tiers simultaneously.",
      marketDominator:
        "Full-funnel push from save-the-date through gate-open. The same model that's helped tourism clients drive 45,000+ visits and +1,700% Facebook reach.",
    },
    testimonial: null,
  },
};

export const tourismList = Object.values(tourismCategories);
